import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '@/stores/appStore';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  UserCircle, 
  Phone, 
  Mail, 
  Calendar,
  Wallet,
  ClipboardCheck,
  HeartHandshake,
  Edit,
  Save,
  X
} from 'lucide-react';
import type { Member } from '@/types';
import { memberTitles, currencySymbols } from '@/types';
import { format } from 'date-fns';

interface MemberSnapshot {
  attendance: {
    total_meetings: number;
    attended: number;
    missed: number;
    rate: number;
  };
  dues: {
    total_paid: number;
    outstanding: number;
    credit: number;
  };
  donations: {
    events_contributed: number;
    total_amount: number;
  };
}

interface DuesPayment {
  id: string;
  amount: number;
  payment_date: string;
  payment_method?: string;
  notes?: string;
  reference_number?: string;
}

export default function MemberDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentOrganization, updateMember } = useAppStore();
  
  const [member, setMember] = useState<Member | null>(null);
  const [snapshot, setSnapshot] = useState<MemberSnapshot | null>(null);
  const [duesPayments, setDuesPayments] = useState<DuesPayment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Edit form state
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [title, setTitle] = useState('');
  const [status, setStatus] = useState<'active' | 'inactive' | 'suspended'>('active');

  const currencySymbol = currentOrganization?.currency 
    ? currencySymbols[currentOrganization.currency as keyof typeof currencySymbols] || '$'
    : '$';

  useEffect(() => {
    if (id) {
      fetchMember();
    }
  }, [id, currentOrganization]);

  const fetchMember = async () => {
    if (!id || !currentOrganization) return;

    setIsLoading(true);
    try {
      // Fetch member
      const { data: memberData, error: memberError } = await supabase
        .from('members')
        .select('*')
        .eq('id', id)
        .single();

      if (memberError) throw memberError;
      
      setMember(memberData as Member);
      setFullName(memberData.full_name);
      setPhone(memberData.phone || '');
      setEmail(memberData.email || '');
      setTitle(memberData.title || 'Member');
      setStatus(memberData.status);

      // Fetch attendance stats
      const { data: attendanceData } = await supabase
        .from('attendance')
        .select('is_present')
        .eq('member_id', id);

      const totalMeetings = attendanceData?.length || 0;
      const attended = attendanceData?.filter(a => a.is_present).length || 0;

      // Fetch dues stats
      const { data: paymentsData } = await supabase
        .from('payments')
        .select('amount')
        .eq('member_id', id)
        .eq('payment_type', 'dues');

      const { data: duesPaymentsData } = await supabase
        .from('payments')
        .select('id, amount, payment_date, payment_method, notes, reference_number')
        .eq('member_id', id)
        .eq('payment_type', 'dues')
        .order('payment_date', { ascending: false });

      const totalPaid = paymentsData?.reduce((sum, p) => sum + (p.amount || 0), 0) || 0;
      setDuesPayments((duesPaymentsData as DuesPayment[]) || []);

      // Fetch donation stats
      const { data: donationsData } = await supabase
        .from('donations')
        .select('amount')
        .eq('member_id', id);

      const totalDonations = donationsData?.reduce((sum, d) => sum + (d.amount || 0), 0) || 0;

      setSnapshot({
        attendance: {
          total_meetings: totalMeetings,
          attended,
          missed: totalMeetings - attended,
          rate: totalMeetings > 0 ? Math.round((attended / totalMeetings) * 100) : 0,
        },
        dues: {
          total_paid: totalPaid,
          outstanding: memberData.dues_balance || 0,
          credit: memberData.credit_balance || 0,
        },
        donations: {
          events_contributed: donationsData?.length || 0,
          total_amount: totalDonations,
        },
      });
    } catch (error) {
      console.error('Error fetching member:', error);
      toast.error(t('errors.generic'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!id || !fullName.trim()) return;

    setIsSaving(true);
    try {
      const { data, error } = await supabase
        .from('members')
        .update({
          full_name: fullName.trim(),
          phone: phone || null,
          email: email || null,
          title: title || 'Member',
          status,
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      setMember(data as Member);
      updateMember(data as Member);
      toast.success(t('members.updateSuccess'));
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating member:', error);
      toast.error(error instanceof Error ? error.message : t('errors.generic'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (!member) return;
    setFullName(member.full_name);
    setPhone(member.phone || '');
    setEmail(member.email || '');
    setTitle(member.title || 'Member');
    setStatus(member.status ?? 'active');
    setIsEditing(false);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!member) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Member not found</p>
        <Button onClick={() => navigate('/members')} className="mt-4">
          Back to Members
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={() => navigate('/members')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        {!isEditing ? (
          <Button onClick={() => setIsEditing(true)}>
            <Edit className="w-4 h-4 mr-2" />
            Edit
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleCancel}>
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaving}>
              <Save className="w-4 h-4 mr-2" />
              Save
            </Button>
          </div>
        )}
      </div>

      {/* Profile Card */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row items-start gap-6">
            <div className="w-24 h-24 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
              <UserCircle className="w-12 h-12 text-primary" />
            </div>
            <div className="flex-1 w-full">
              {isEditing ? (
                <div className="space-y-4">
                  <div>
                    <Label>Full Name</Label>
                    <Input
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Title</Label>
                      <Select value={title} onValueChange={setTitle}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {memberTitles.map((t) => (
                            <SelectItem key={t} value={t}>{t}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Status</Label>
                      <Select value={status} onValueChange={(v) => setStatus(v as 'active' | 'inactive' | 'suspended')}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="inactive">Inactive</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-start justify-between">
                    <div>
                      <h1 className="text-2xl font-bold">{member.full_name}</h1>
                      <p className="text-muted-foreground">{member.title}</p>
                    </div>
                    <Badge variant={member.status === 'active' ? 'default' : 'secondary'}>
                      {member.status}
                    </Badge>
                  </div>
                  
                  <div className="mt-4 space-y-2">
                    {member.phone && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Phone className="w-4 h-4" />
                        <span>{member.phone}</span>
                      </div>
                    )}
                    {member.email && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Mail className="w-4 h-4" />
                        <span>{member.email}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Calendar className="w-4 h-4" />
                      <span>Joined {format(new Date(member.join_date), 'MMM dd, yyyy')}</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Tabs */}
      {!isEditing && (
        <Tabs defaultValue="attendance">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="attendance">
              <ClipboardCheck className="w-4 h-4 mr-2" />
              Attendance
            </TabsTrigger>
            <TabsTrigger value="dues">
              <Wallet className="w-4 h-4 mr-2" />
              Dues
            </TabsTrigger>
            <TabsTrigger value="donations">
              <HeartHandshake className="w-4 h-4 mr-2" />
              Donations
            </TabsTrigger>
          </TabsList>

          <TabsContent value="attendance">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Attendance Record</CardTitle>
                <CardDescription>Meeting attendance statistics</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <StatBox 
                    label="Total Meetings" 
                    value={snapshot?.attendance.total_meetings || 0} 
                  />
                  <StatBox 
                    label="Attended" 
                    value={snapshot?.attendance.attended || 0}
                    className="text-green-600"
                  />
                  <StatBox 
                    label="Missed" 
                    value={snapshot?.attendance.missed || 0}
                    className="text-red-600"
                  />
                  <StatBox 
                    label="Attendance Rate" 
                    value={`${snapshot?.attendance.rate || 0}%`}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="dues">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Dues Summary</CardTitle>
                <CardDescription>Payment history and balance</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  <StatBox 
                    label="Total Paid" 
                    value={`${currencySymbol}${(snapshot?.dues.total_paid || 0).toLocaleString()}`}
                    className="text-green-600"
                  />
                  <StatBox 
                    label="Outstanding" 
                    value={`${currencySymbol}${(snapshot?.dues.outstanding || 0).toLocaleString()}`}
                    className="text-red-600"
                  />
                  <StatBox 
                    label="Credit Balance" 
                    value={`${currencySymbol}${(snapshot?.dues.credit || 0).toLocaleString()}`}
                    className="text-blue-600"
                  />
                </div>

                {((snapshot?.dues.outstanding || 0) > 0 || (snapshot?.dues.credit || 0) > 0) && (
                  <div className="space-y-2 mt-4">
                    {(snapshot?.dues.outstanding || 0) > 0 && (
                      <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                        Outstanding balance: {currencySymbol}{(snapshot?.dues.outstanding || 0).toLocaleString()} — member owes dues
                      </div>
                    )}
                    {(snapshot?.dues.credit || 0) > 0 && (
                      <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
                        Credit balance: {currencySymbol}{(snapshot?.dues.credit || 0).toLocaleString()} — prepaid ahead
                      </div>
                    )}
                  </div>
                )}

                <div className="mt-6">
                  <h3 className="font-semibold mb-3">Payment History</h3>
                  {duesPayments.length > 0 ? (
                    <div className="space-y-2">
                      {duesPayments.map((payment) => (
                        <div key={payment.id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                          <div>
                            <p className="text-sm">
                              {format(new Date(payment.payment_date), 'MMM dd, yyyy')}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {payment.payment_method || 'N/A'}
                            </p>
                          </div>
                          <p className="font-semibold text-green-600">
                            {currencySymbol}{payment.amount.toLocaleString()}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No dues payments recorded yet.</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="donations">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Donation History</CardTitle>
                <CardDescription>Contributions to donation events</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <StatBox 
                    label="Events Contributed" 
                    value={snapshot?.donations.events_contributed || 0}
                  />
                  <StatBox 
                    label="Total Donated" 
                    value={`${currencySymbol}${(snapshot?.donations.total_amount || 0).toLocaleString()}`}
                    className="text-green-600"
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

function StatBox({ label, value, className }: { label: string; value: string | number; className?: string }) {
  return (
    <div className="text-center p-4 bg-muted rounded-lg">
      <p className={`text-2xl font-bold ${className}`}>{value}</p>
      <p className="text-sm text-muted-foreground">{label}</p>
    </div>
  );
}
